import { useEffect, useMemo, useRef, useState } from "react";
import MapCanvas, { type MapCamera, type MapCanvasHandle, type MapClient } from "./MapCanvas";
import { overlayThemeVariables } from "../overlayTheme";

type FrameState = {
  clients: MapClient[];
  center: { lat: number; lng: number };
  zoom: number;
  markerLabelMinZoom: number;
  mapLabel: string;
  satelliteLabel: string;
  accentColor: string;
  overlayActionBackgroundColor: string;
  mapDimStrength: number;
  optimizedTileLoading: boolean;
  visibleClientIds: number[];
  highlightedClientId: number | null;
};

const initialState: FrameState = {
  clients: [],
  center: { lat: 37.09, lng: -8.24 },
  zoom: 9,
  markerLabelMinZoom: 11,
  mapLabel: "Map",
  satelliteLabel: "Satellite",
  accentColor: "#9c6f3c",
  overlayActionBackgroundColor: "#f7f5ef",
  mapDimStrength: 35,
  optimizedTileLoading: true,
  visibleClientIds: [],
  highlightedClientId: null
};

function notifyParent(message: Record<string, unknown>) {
  window.parent.postMessage(message, window.location.origin);
}

export default function MapFrame() {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);
  const map = useRef<MapCanvasHandle | null>(null);
  stateRef.current = state;

  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "SHOWROOM_MAP_DATA") {
        setState((current) => ({ ...current, clients: event.data.clients || [] }));
        return;
      }
      if (event.data?.type === "SHOWROOM_MAP_CONFIG") {
        setState((current) => ({ ...current, ...event.data.config }));
        return;
      }
      if (event.data?.type === "SHOWROOM_MAP_MARKERS") {
        setState((current) => ({
          ...current,
          visibleClientIds: event.data.visibleClientIds || [],
          highlightedClientId: event.data.highlightedClientId ?? null
        }));
        return;
      }
      if (event.data?.type !== "SHOWROOM_MAP_COMMAND") return;

      const command = event.data.command;
      if (command === "focusClient") {
        const client = stateRef.current.clients.find((item) => item.id === Number(event.data.clientId));
        if (client) {
          void map.current?.focusClient(client, Number(event.data.zoom)).then(() => {
            notifyParent({ type: "SHOWROOM_MAP_COMMAND_DONE", requestId: event.data.requestId });
          });
        }
      }
      if (command === "resetHome") {
        map.current?.resetHome(event.data.center, Number(event.data.zoom), true);
      }
      if (command === "restoreCamera") {
        map.current?.restoreCamera(event.data.camera);
      }
    };

    window.addEventListener("message", receiveMessage);
    notifyParent({ type: "SHOWROOM_MAP_READY" });
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  const visibleClientIds = useMemo(() => new Set(state.visibleClientIds), [state.visibleClientIds]);

  return <main className="map-frame-page" style={{
    ...overlayThemeVariables(state.overlayActionBackgroundColor),
    "--accent": state.accentColor,
    "--map-dim-opacity": state.mapDimStrength / 100
  } as React.CSSProperties}>
    <MapCanvas
      ref={map}
      clients={state.clients}
      center={state.center}
      zoom={state.zoom}
      markerLabelMinZoom={state.markerLabelMinZoom}
      mapLabel={state.mapLabel}
      satelliteLabel={state.satelliteLabel}
      optimizedTileLoading={state.optimizedTileLoading}
      visibleClientIds={visibleClientIds}
      highlightedClientId={state.highlightedClientId}
      onSelect={(client) => notifyParent({ type: "SHOWROOM_MAP_SELECT", clientId: client.id })}
      onActivity={() => notifyParent({ type: "SHOWROOM_MAP_ACTIVITY" })}
      onCameraChange={(camera: MapCamera) => notifyParent({ type: "SHOWROOM_MAP_CAMERA", camera })}
    />
  </main>;
}
